/**
 * SpinningGlobe — lightweight canvas-rendered wireframe globe.
 *
 * Rationale:
 * - Slow Y-axis rotation, front-hemisphere only (z > 0 after rotation).
 * - Points are distributed via a Fibonacci-sphere algorithm so coverage
 *   is visually uniform without a lat/lng grid's polar clumping.
 * - Each point connects to its K nearest neighbors on the sphere, which
 *   produces the triangulated "network mesh" look from the inspo images
 *   without a Delaunay pass or a heavy 3D library.
 * - Alpha ramps with depth so points near the silhouette fade smoothly
 *   into the background — no hard edges, reads as a real 3D sphere.
 *
 * No three.js; this is ~200 lines of vanilla 2D canvas. Respects the
 * user's prefers-reduced-motion setting by pausing rotation.
 */

import React, { useEffect, useRef } from 'react';

interface SpinningGlobeProps {
  /** Diameter in px for the rendered canvas. Canvas is square. */
  size?: number;
  /** Color for lines + dots. Accepts any CSS color. */
  color?: string;
  /** Rotation speed in radians per second. Low is better — the globe is
   * meant to be background atmosphere, not a focal animation. */
  rotationSpeed?: number;
  /** Number of vertex points distributed on the sphere. 400-600 is the
   * sweet spot for a detailed-but-not-busy look. */
  pointCount?: number;
  /** Each point connects to its K nearest neighbors. Higher = denser mesh. */
  neighbors?: number;
  /** Optional CSS style overrides. */
  style?: React.CSSProperties;
}

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Fibonacci-sphere: N points nearly-uniformly distributed on unit sphere.
 * Standard trick — golden-angle increments in longitude + linear latitude. */
function fibonacciSphere(n: number): Vec3[] {
  const points: Vec3[] = [];
  const phi = Math.PI * (3 - Math.sqrt(5)); // golden angle
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2; // y from 1 to -1
    const r = Math.sqrt(1 - y * y);
    const theta = phi * i;
    points.push({ x: Math.cos(theta) * r, y, z: Math.sin(theta) * r });
  }
  return points;
}

/** For each point, return indexes of its K nearest neighbors on the sphere.
 * O(N²) — fine for N≈500 (250k comparisons once at mount, not per frame). */
function buildNeighborIndex(points: Vec3[], k: number): number[][] {
  const out: number[][] = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    // Track (distSquared, index) pairs; keep smallest K.
    const dists: Array<[number, number]> = [];
    for (let j = 0; j < points.length; j++) {
      if (j === i) continue;
      const q = points[j];
      const dx = p.x - q.x, dy = p.y - q.y, dz = p.z - q.z;
      dists.push([dx * dx + dy * dy + dz * dz, j]);
    }
    dists.sort((a, b) => a[0] - b[0]);
    out.push(dists.slice(0, k).map(([, idx]) => idx));
  }
  return out;
}

export function SpinningGlobe({
  size = 520,
  color = '#44ccff',
  rotationSpeed = 0.15,
  pointCount = 480,
  neighbors = 4,
  style,
}: SpinningGlobeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);

  // Generate the geometry once — these arrays persist for the component's
  // lifetime since the sphere itself doesn't change, only its rotation.
  const geometryRef = useRef<{ points: Vec3[]; neighborIdx: number[][] } | null>(null);
  if (!geometryRef.current) {
    const points = fibonacciSphere(pointCount);
    const neighborIdx = buildNeighborIndex(points, neighbors);
    geometryRef.current = { points, neighborIdx };
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Handle high-DPI displays without making the canvas enormous in CSS.
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const cx = size / 2;
    const cy = size / 2;
    const radius = size * 0.42; // leaves a little margin so points don't clip at the edge
    const { points, neighborIdx } = geometryRef.current!;

    let prevTs: number | null = null;
    let angle = 0;

    // Reusable scratch buffer for rotated projections so we don't allocate
    // an array every frame at 60fps.
    const projected = new Array<{ sx: number; sy: number; depth: number }>(points.length);
    for (let i = 0; i < points.length; i++) projected[i] = { sx: 0, sy: 0, depth: 0 };

    const draw = (ts: number) => {
      if (prevTs == null) prevTs = ts;
      const dt = (ts - prevTs) / 1000;
      prevTs = ts;
      if (!reduceMotion) angle += rotationSpeed * dt;

      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      // Project every point to screen space up front. Standard Y-axis rotation:
      //   x' = x·cos + z·sin,  z' = -x·sin + z·cos,  y' = y.
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const rx = p.x * cos + p.z * sin;
        const rz = -p.x * sin + p.z * cos;
        projected[i].sx = cx + rx * radius;
        projected[i].sy = cy + p.y * radius;
        projected[i].depth = rz; // -1 (far) to 1 (near)
      }

      ctx.clearRect(0, 0, size, size);

      // Draw neighbor connections. Only render edges where BOTH endpoints
      // are in the front hemisphere (depth > 0) so back-of-sphere lines
      // don't bleed through the silhouette. Alpha ramps with average depth
      // for atmospheric fade near the edge.
      ctx.lineWidth = 0.6;
      for (let i = 0; i < points.length; i++) {
        const a = projected[i];
        if (a.depth < 0) continue;
        const neighborList = neighborIdx[i];
        for (const j of neighborList) {
          if (j <= i) continue; // draw each edge once
          const b = projected[j];
          if (b.depth < 0) continue;
          const avg = (a.depth + b.depth) * 0.5;
          const alpha = Math.pow(avg, 0.9) * 0.55; // 0..0.55
          ctx.strokeStyle = hexToRgba(color, alpha);
          ctx.beginPath();
          ctx.moveTo(a.sx, a.sy);
          ctx.lineTo(b.sx, b.sy);
          ctx.stroke();
        }
      }

      // Draw points on top of the mesh. Same front-hemisphere filter;
      // brighter + slightly larger near the center of the facing disc.
      for (let i = 0; i < points.length; i++) {
        const a = projected[i];
        if (a.depth < 0) continue;
        const alpha = Math.pow(a.depth, 0.7) * 0.9;
        const rPx = 0.5 + a.depth * 1.1;
        ctx.fillStyle = hexToRgba(color, alpha);
        ctx.beginPath();
        ctx.arc(a.sx, a.sy, rPx, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [size, color, rotationSpeed]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: 'block',
        pointerEvents: 'none',
        ...style,
      }}
      aria-hidden
    />
  );
}

/** Convert a #RRGGBB hex string to rgba(…) with the given alpha (0..1).
 * Falls back to passing the input through when it's already non-hex so
 * callers can pass rgb(..) or named colors if they want. */
function hexToRgba(hex: string, alpha: number): string {
  if (!hex.startsWith('#')) return hex;
  const h = hex.slice(1);
  const full = h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha)).toFixed(3)})`;
}
